import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Globe, FileText, BarChart2, RefreshCw, CheckCircle, AlertCircle, Smartphone, Lock, Zap, PenTool, Plus, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WebsiteManagerPage = () => {
  const [overview, setOverview] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, perfRes] = await Promise.all([
        axios.get(`${API_URL}/api/website/overview`),
        axios.get(`${API_URL}/api/website/performance`),
      ]);
      setOverview(overviewRes.data);
      setPerformance(perfRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-brand';
    if (score >= 50) return 'text-amber-500';
    return 'text-red-500';
  };

  const getGradeColor = (grade) => {
    if (grade === 'A') return 'text-emerald-brand bg-emerald-brand/10';
    if (grade === 'B') return 'text-blue-500 bg-blue-500/10';
    if (grade === 'C') return 'text-amber-500 bg-amber-500/10';
    return 'text-red-500 bg-red-500/10';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="website-manager-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Website Manager<PageInfoTooltip pageKey="website-manager" /></h1>
          <p className="text-gray-medium">Monitor and optimize your website</p>
        </div>
        <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2">
          <Plus className="h-4 w-4" />
          Create Blog Post
        </Button>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-white border border-gray-border w-full sm:w-auto overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="overview" className="text-xs sm:text-sm whitespace-nowrap">Overview</TabsTrigger>
          <TabsTrigger value="blog" className="text-xs sm:text-sm whitespace-nowrap">Blog</TabsTrigger>
          <TabsTrigger value="performance" className="text-xs sm:text-sm whitespace-nowrap">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Health Score */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Card className="bg-white border-gray-border col-span-2 sm:col-span-1">
              <CardContent className="p-4 sm:p-5 text-center">
                <div className={cn("text-3xl sm:text-4xl font-heading font-bold mb-2", getScoreColor(overview?.health_score || 0))}>
                  {overview?.health_score || 0}
                </div>
                <Progress value={overview?.health_score || 0} className="mb-2" />
                <p className="text-xs sm:text-sm text-gray-medium">Website Health</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-gray-border">
              <CardContent className="p-3 sm:p-5 flex items-center gap-3 sm:gap-4">
                <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0", overview?.ssl_valid ? 'bg-emerald-brand/10' : 'bg-red-500/10')}>
                  <Lock className={cn("h-5 w-5 sm:h-6 sm:w-6", overview?.ssl_valid ? 'text-emerald-brand' : 'text-red-500')} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary">SSL</p>
                  <Badge className={cn("text-[10px]", overview?.ssl_valid ? 'bg-emerald-brand' : 'bg-red-500')}>
                    {overview?.ssl_valid ? 'Valid' : 'Invalid'}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-gray-border">
              <CardContent className="p-3 sm:p-5 flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary">Speed</p>
                  <p className={cn("text-lg sm:text-2xl font-bold", getScoreColor(overview?.page_speed_score || 0))}>
                    {overview?.page_speed_score || 0}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border-gray-border">
              <CardContent className="p-3 sm:p-5 flex items-center gap-3 sm:gap-4">
                <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0", overview?.mobile_friendly ? 'bg-emerald-brand/10' : 'bg-amber-500/10')}>
                  <Smartphone className={cn("h-5 w-5 sm:h-6 sm:w-6", overview?.mobile_friendly ? 'text-emerald-brand' : 'text-amber-500')} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary">Mobile</p>
                  <Badge className={cn("text-[10px]", overview?.mobile_friendly ? 'bg-emerald-brand' : 'bg-amber-500')}>
                    {overview?.mobile_friendly ? 'Pass' : 'Needs Work'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Changes */}
          <Card className="bg-white border-gray-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Recent Changes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-medium text-center py-8">No recent changes recorded</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blog">
          <Card className="bg-white border-gray-border">
            <CardContent className="p-8 text-center">
              <PenTool className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-medium">No blog posts yet</p>
              <p className="text-sm text-gray-medium mb-4">Create AI-generated blog content</p>
              <Button className="bg-emerald-brand hover:bg-emerald-hover">
                <Sparkles className="h-4 w-4 mr-2" />
                Create with AI
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          {/* Core Web Vitals */}
          <Card className="bg-white border-gray-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Core Web Vitals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {performance?.core_web_vitals && Object.entries(performance.core_web_vitals).map(([key, data]) => (
                  <div key={key} className="text-center">
                    <div className={cn("inline-flex items-center justify-center w-16 h-16 rounded-full text-xl font-bold mb-2", getGradeColor(data.grade))}>
                      {data.grade}
                    </div>
                    <p className="font-medium text-text-primary uppercase">{key}</p>
                    <p className="text-sm text-gray-medium">{data.value}{key === 'cls' ? '' : 's'}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Page Speed */}
          <Card className="bg-white border-gray-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Page Speed Scores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-medium">Mobile</span>
                    <span className={cn("font-bold", getScoreColor(performance?.page_speed?.mobile || 0))}>
                      {performance?.page_speed?.mobile || 0}
                    </span>
                  </div>
                  <Progress value={performance?.page_speed?.mobile || 0} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-medium">Desktop</span>
                    <span className={cn("font-bold", getScoreColor(performance?.page_speed?.desktop || 0))}>
                      {performance?.page_speed?.desktop || 0}
                    </span>
                  </div>
                  <Progress value={performance?.page_speed?.desktop || 0} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WebsiteManagerPage;
